import { ContactTeamButton } from '@/app/components/common/ContactTeamButton';
import { FAQ } from '@/app/components/faq/FAQ';
import useCollectPaymentMethod from '@/app/components/payment/components/method/payment-method-v2.controller';
import usePaymentController from '@/app/components/payment/payment.controller';
import CustomLink from '@/components/custom-link/CustomLink';
import customTheme from '@/constants/theme.custom';
import { Urls } from '@/constants/urls';
import useAnalyticsCalls from '@/hooks/use-analytics';
import useOnMount from '@/hooks/useOnMount';
import { useSetIntercomIsVisible } from '@/queries/layout.queries';
import { Divider, Flex } from '@chakra-ui/react';
import { Box, Section, Text } from '@cmpsr/components';
import { Elements } from '@stripe/react-stripe-js';
import { useRef, useState } from 'react';
import { UseFormReturn } from 'react-hook-form';
import { Outlet, useNavigate } from 'react-router-dom';
import DoubleWave from '../ppra-loading-page/components/DoubleWave';
import EmailSection from './components/EmailSection';
import InviteCodeForm from './components/InviteCodeForm';
import MembershipSummary from './components/MembershipSummary';
import PaymentSection from './components/PaymentSection';
import PurchaseSection from './components/PurchaseSection';
import { smallItalicText, styleTextH4 } from './components/styles';
import TermsSection from './components/TermsSection';
import { SignUpStepper } from '@/app/components/sign-up/components/SignUpStepper';
import { VARS } from '../common/constants';

export type PaymentFormState = {
  email: string;
  autoRenewal: boolean;
  coupon: string;
  zip: string;
  inviteCode?: string;
};

export type PaymentFormComponentsProps = {
  form: UseFormReturn<PaymentFormState>;
};

export type InviteCodeFormProps = {
  email: string;
  isEmailEditable: boolean;
  setIsEmailEditable: (isEmailEditable: boolean) => void;
};

export const PaymentForm = () => {
  const {
    form,
    handleSubmit,
    priceData,
    isCouponVisible,
    showCoupon,
    coupon,
    setCoupon,
    couponValidationError,
    isSubmitting,
    getPrice,
    joinWaitlist,
    isEmailEditable,
    setIsEmailEditable,
    email,
    inviteCode,
    currentPrice,
    isPpraInviteRequired,
    clearedWaitList,
  } = useCollectPaymentMethod();

  // State variables for payment section
  const [isPaymentComplete, setIsPaymentComplete] = useState(false);
  const [showPaymentError, setShowPaymentError] = useState(false);

  const paymentSectionRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();

  const handleBackClick = () => {
    navigate(-1);
  };

  if (isPpraInviteRequired && !clearedWaitList) {
    return (
      <InviteCodeForm
        email={email}
        isEmailEditable={isEmailEditable}
        setIsEmailEditable={setIsEmailEditable}
        form={form}
        inviteCode={inviteCode}
        joinWaitlist={joinWaitlist}
        isPpraInviteRequired={isPpraInviteRequired}
        handleBackClick={handleBackClick}
      />
    );
  }

  return (
    <Box
      width="100%"
      minHeight="100vh"
      background="white"
      display="flex"
      flexDirection="column"
      paddingBottom="spacer-48"
      position="relative"
    >
      <Box position="absolute" top={0} left={0} right={0} bottom={0} zIndex={1}>
        <DoubleWave />
      </Box>
      <Box position="relative" zIndex={2} width="100%" display="flex" flexDirection="column">
        <Box width="100%" my={{ base: '5', md: '10' }} maxWidth="550px" mx="auto">
          <SignUpStepper activeStep={0} />
        </Box>
        <Box
          display="flex"
          flexDirection="column"
          alignItems="center"
          maxWidth={{ base: '315px', lg: '600px' }}
          mx="auto"
          width="100%"
          gap="25px"
          pt="4"
        >
          <EmailSection
            email={email}
            isEmailEditable={isEmailEditable}
            setIsEmailEditable={setIsEmailEditable}
            form={form}
            inviteCode={inviteCode}
            joinWaitlist={joinWaitlist}
            isPpraInviteRequired={isPpraInviteRequired}
          />

          <Divider borderColor={customTheme.colors.lightBlue} />

          <PaymentSection
            setIsPaymentComplete={setIsPaymentComplete}
            showPaymentError={showPaymentError}
            setShowPaymentError={setShowPaymentError}
            paymentSectionRef={paymentSectionRef}
            form={form}
            getPrice={getPrice}
          />

          <MembershipSummary
            priceData={priceData}
            isCouponVisible={isCouponVisible}
            showCoupon={showCoupon}
            coupon={coupon}
            setCoupon={setCoupon}
            couponValidationError={couponValidationError}
            isSubmitting={isSubmitting}
            getPrice={getPrice}
          />

          <Box width="100%">
            <TermsSection form={form} currentPrice={currentPrice} />
          </Box>

          <Box width="100%">
            <PurchaseSection
              form={form}
              isPaymentComplete={isPaymentComplete}
              currentPrice={currentPrice}
              setShowPaymentError={setShowPaymentError}
              paymentSectionRef={paymentSectionRef}
              handleSubmit={handleSubmit}
              isSubmitting={isSubmitting}
            />
          </Box>

          <Text {...smallItalicText} color="darkBlue">
            By clicking {VARS.cta.message}, you agree to our{' '}
            <CustomLink href={Urls.terms_of_service} target="_blank" {...smallItalicText} color="darkBlue">
              Terms of Service
            </CustomLink>{' '}
            & consent to the{' '}
            <CustomLink href={Urls.privacy_policy} target="_blank" {...smallItalicText} color="darkBlue">
              Privacy Policy
            </CustomLink>
          </Text>
        </Box>
        <Section w="90%" mx="auto" mb={{ base: '0', md: '60' }} mt={{ base: '4', md: '10' }}>
          <Flex justifyContent="center" alignItems="center" flexDirection="column" gap="25px">
            <Box>
              <Text {...styleTextH4}>Frequently Asked Questions</Text>
              <FAQ variant="dark" />
            </Box>
            <Text
              color="white"
              fontSize="30px"
              fontWeight="400"
              lineHeight="130%"
              textAlign="center"
              fontFamily={customTheme.typography.fonts.inter}
            >
              Have Questions?
            </Text>
            <Box width="600px">
              <ContactTeamButton variant="light" />
            </Box>
          </Flex>
        </Section>
      </Box>
    </Box>
  );
};

export const PPRAPaymentPage = () => {
  const { stripeOptions, stripePromise } = usePaymentController('default');
  const setIntercomIsVisible = useSetIntercomIsVisible();

  useOnMount(() => {
    setIntercomIsVisible.mutate(false);
    return () => {
      setIntercomIsVisible.mutate(true);
    };
  });

  return (
    <Elements options={stripeOptions} stripe={stripePromise}>
      <Outlet />
    </Elements>
  );
};
